import type { App } from '../../../app/App';
import { motion } from '../../animation/Motion';
import { cosmeticDef } from '../../data/cosmetics';
import { FORTUNE_COLORS, FORTUNE_NUMBERS, FORTUNE_RULES, FORTUNE_TIERS, type FortuneTier } from '../../data/fortune';
import { claimFreeTicket, coinDrawsLeft, collection, drawFive, drawFortune, freeTicketWaiting, nextPayment, spellLetters, type FortuneResult } from '../../systems/fortune';
import { stampIcon } from '../../../drawing/stamps';
import { artImg, button, modal } from '../../../ui/components';
import { stampFace } from '../../../ui/cosmetics';
import { h, setChildren } from '../../../ui/dom';
import { fortuneTeller, type TellerPose } from '../../../ui/fortuneTeller';
import { icon } from '../../../ui/icons';
import { t } from '../../../i18n';
import type { NotebookCtx } from './tabs';

/**
 * Paper fortune teller (Notebook → Gear, and a note on the home page when
 * today's free ticket is waiting). Played like the real toy: pick a colour,
 * it opens once per letter; pick a number, it opens that many times; lift a
 * flap for the prize. The choices are for fun only: systems/fortune.ts rolls
 * the prize with the odds shown in the odds panel.
 */

/** One open of the teller while spelling or counting. */
const STEP_MS = 280;
/** Long words and big numbers only animate their last opens. */
const MAX_STEPS = 8;

const TIER_LABEL: Record<FortuneTier, string> = { common: 'Common', rare: 'Rare', epic: 'Epic', legendary: 'Legendary' };

const sleep = (ms: number): Promise<void> => new Promise((r) => window.setTimeout(r, ms));

const ticketLine = (n: number): HTMLElement =>
  h('span', { class: 'ft-tickets' }, icon('ticket', 'ft-ticket-icon'), ` ${t('Tickets: {n} / {max}', { n, max: FORTUNE_RULES.ticketCap })}`);

/** The odds exactly as rolled, plus the house rules (guarantees, coins instead of a duplicate). */
export function fortuneOdds(): HTMLElement {
  const { pity, refund } = FORTUNE_RULES;
  return h(
    'details',
    { class: 'ft-odds' },
    h('summary', null, t('Odds: what can I win?')),
    h(
      'ul',
      null,
      FORTUNE_TIERS.map((d) => h('li', { class: `tier-${d.tier}` }, h('b', null, `${t(TIER_LABEL[d.tier])} ${d.percent}%`), ' · ', t(d.prize))),
    ),
    h('p', null, t('Guaranteed: at least Rare every {rare} draws, Epic every {epic} draws and Legendary every {legendary} draws in a row without one.', pity)),
    h('p', null, t('No duplicates: you only win things you do not have yet. When a whole tier is collected you get coins instead ({common} / {rare} / {epic} / {legendary}).', refund)),
    h('p', null, t('Colours, numbers and flaps are just for fun: every draw has the same odds.')),
    h('p', null, t('Draws use free tickets or coins earned in play. They are never sold for real money.')),
  );
}

const earnHint = (): HTMLElement =>
  h(
    'p',
    { class: 'hand-hint ft-earn' },
    t("Free tickets: one every day, and one more for finishing all of today's quests (keep up to {max}). No ticket? A draw costs {cost} coins, or 5 draws {five} coins (up to {limit} coin draws a day).", {
      max: FORTUNE_RULES.ticketCap,
      cost: FORTUNE_RULES.drawCost,
      five: FORTUNE_RULES.fiveCost,
      limit: FORTUNE_RULES.dailyCoinDraws,
    }),
  );

/** "12 / 66 prizes collected" with a little bar. */
function collectionLine(app: App): HTMLElement {
  const c = collection(app.store.state);
  return h('p', { class: 'ft-collection' }, h('b', null, t('Collection: {owned} / {total}', c)), h('span', { class: 'ft-collection-bar', style: { '--p': `${Math.round((c.owned / Math.max(1, c.total)) * 100)}%` } as unknown as Partial<CSSStyleDeclaration> }));
}

/** What the Play button costs right now. */
function playLabel(app: App): string {
  const pay = nextPayment(app.store.state);
  return pay === 'coins' ? t('Play! ({n} coins)', { n: FORTUNE_RULES.drawCost }) : t('Play!');
}

/** The Gear page section: tickets, a play button and the odds. */
export function fortuneSection(ctx: NotebookCtx): HTMLElement {
  const { app } = ctx;
  const save = app.store.state;
  const free = freeTicketWaiting(save);
  const canPlay = free || nextPayment(save) !== null;
  const canFive = save.coins >= FORTUNE_RULES.fiveCost && coinDrawsLeft(save) >= 5;
  return h(
    'section',
    { class: 'fortune-section' },
    h('h3', { class: 'section-title' }, t('Paper Fortune Teller')),
    earnHint(),
    h(
      'div',
      { class: 'ft-section-row' },
      ticketLine(save.fortune.tickets),
      free ? h('span', { class: 'sticky-note pink ft-free' }, t('Free ticket waiting!')) : null,
      button(free ? t('Play!') : playLabel(app), {
        variant: 'primary',
        color: 'pink',
        disabled: !canPlay,
        title: canPlay ? undefined : t('No tickets and not enough coins yet. Win some battles and come back!'),
        sfx: app,
        onClick: () => void openFortuneTeller(app),
      }),
      button(t('×5 for {n} coins', { n: FORTUNE_RULES.fiveCost }), {
        color: 'yellow',
        disabled: !canFive,
        title: canFive ? t('Five quick draws at once') : coinDrawsLeft(save) < 5 ? t('That is enough draws for today. Come back tomorrow!') : t('Not enough coins yet'),
        sfx: app,
        onClick: () => void openFiveDraws(app, ctx),
      }),
    ),
    collectionLine(app),
    fortuneOdds(),
  );
}

/** Home page note while today's free ticket has not been picked up. */
export function fortuneNudge(ctx: NotebookCtx): HTMLElement | null {
  const { app } = ctx;
  if (!freeTicketWaiting(app.store.state)) return null;
  const open = (): void => void openFortuneTeller(app);
  return h(
    'aside',
    {
      class: 'fortune-nudge sticky-note pink',
      role: 'button',
      tabindex: '0',
      title: t('Tap to play the paper fortune teller.'),
      on: { click: open, keydown: (e: KeyboardEvent) => (e.key === 'Enter' || e.key === ' ') && open() },
    },
    icon('ticket', 'ft-ticket-icon'),
    h('b', null, t('A free fortune is waiting!')),
  );
}

function prizeVisual(app: App, id: string | null): Node {
  const def = id ? cosmeticDef(id) : undefined;
  if (!def) return icon('coin', 'ft-coin');
  if (def.kind === 'pen') return h('span', { class: 'pen-swatch', style: { '--ink': def.color } as unknown as Partial<CSSStyleDeclaration>, 'aria-hidden': 'true' });
  if (def.kind === 'brush') return h('span', { class: 'ft-brush', style: { color: '#3b3b3b' } }, stampIcon(def.id.slice('brush:'.length)));
  return stampFace(app, def, 'stamp-preview');
}

function prizeCard(app: App, r: FortuneResult): HTMLElement {
  const def = r.item ? cosmeticDef(r.item) : undefined;
  const visual = prizeVisual(app, r.item);
  return h(
    'div',
    { class: `ft-prize tier-${r.tier}`, role: 'status' },
    artImg(app, `fx:fortune_${r.tier}`, 'ft-glow-art'),
    h('span', { class: 'ft-tier' }, t(TIER_LABEL[r.tier])),
    visual,
    h('b', { class: 'ft-prize-name' }, def ? t('You won the {item}!', { item: t(def.name) }) : t('+{coins} coins', { coins: r.coins })),
    def?.kind === 'brush' ? h('small', null, t('New drawing stamp! Find it in the stamp strip when you draw.')) : null,
    !def ? h('small', null, t('You already have everything of this tier, so here are coins instead.')) : null,
    r.pity ? h('small', null, t('Guaranteed prize! Your lucky streak paid off.')) : null,
    h('p', { class: 'ft-saying' }, `“${t(r.saying)}”`),
  );
}

/** Opens the fortune teller; picks up today's free ticket first. */
export async function openFortuneTeller(app: App): Promise<void> {
  let gotFree = false;
  app.store.update(['inventory'], (s) => {
    gotFree = claimFreeTicket(s);
  });
  if (gotFree) app.toast(t("Here is today's free fortune ticket!"), 'success');

  const toy = fortuneTeller(app);
  const tickets = h('div', { class: 'ft-head' });
  const prompt = h('p', { class: 'ft-prompt', 'aria-live': 'polite' });
  const spell = h('p', { class: 'ft-spell', 'aria-hidden': 'true' });
  const result = h('div', { class: 'ft-result' });
  const body = h('div', { class: 'ft-body' }, tickets, prompt, h('div', { class: 'ft-stage' }, toy.el, spell), result, earnHint(), fortuneOdds());
  const alive = (): boolean => body.isConnected;
  const colors = (): string[] => FORTUNE_COLORS.map((c) => t(c.name));
  const showTickets = (): void => setChildren(tickets, ticketLine(app.store.state.fortune.tickets));

  /**
   * Opens the teller `n` times, alternating up/down (a) and sideways (b),
   * starting after `from`. Returns the last direction. `onStep` gets how many
   * opens have been counted so far.
   */
  const pump = async (n: number, from: 'a' | 'b' | null, onStep: (count: number) => void): Promise<'a' | 'b'> => {
    const first = from === 'a' ? 'b' : 'a';
    const other = first === 'a' ? 'b' : 'a';
    const dirAt = (j: number): 'a' | 'b' => (j % 2 === 0 ? first : other);
    const last = dirAt(n - 1);
    if (motion.reduced) {
      // No pumping: jump straight to where the counting ends.
      toy.setPose(`open_${last}` as TellerPose);
      onStep(n);
      return last;
    }
    for (let j = Math.max(0, n - MAX_STEPS); j < n && alive(); j++) {
      toy.setPose(`open_${dirAt(j)}` as TellerPose);
      app.sfx.play('flip');
      onStep(j + 1);
      await sleep(STEP_MS);
    }
    return last;
  };

  const start = (): void => {
    setChildren(result);
    setChildren(spell);
    showTickets();
    toy.setPose('closed');
    toy.showOutside(colors());
    const pay = nextPayment(app.store.state);
    if (!pay) {
      toy.setPick(null);
      prompt.textContent = t('No tickets and not enough coins left for now. Win some battles and come back!');
      return;
    }
    prompt.textContent = pay === 'coins' ? t('Pick a colour! (this draw costs {n} coins)', { n: FORTUNE_RULES.drawCost }) : t('Pick a colour!');
    toy.setPick((q) => void pickColour(q));
  };

  const pickColour = async (q: number): Promise<void> => {
    toy.setPick(null);
    const letters = spellLetters(colors()[q]!);
    prompt.textContent = t('Spelling {word}…', { word: colors()[q]! });
    const dir = await pump(letters.length, null, (n) => setChildren(spell, letters.slice(0, n).join('-')));
    if (!alive()) return;
    const numbers = FORTUNE_NUMBERS[dir === 'a' ? 'v' : 'h'];
    toy.showInside(numbers);
    prompt.textContent = t('Pick a number!');
    toy.setPick((i) => void pickNumber(numbers[i]!, dir));
  };

  const pickNumber = async (n: number, from: 'a' | 'b'): Promise<void> => {
    toy.setPick(null);
    prompt.textContent = t('Counting to {n}…', { n });
    const dir = await pump(n, from, (c) => setChildren(spell, String(c)));
    if (!alive()) return;
    toy.showInside(FORTUNE_NUMBERS[dir === 'a' ? 'v' : 'h']);
    prompt.textContent = t('Pick a flap to lift!');
    toy.setPick((i) => void lift(i));
  };

  const lift = async (quarter: number): Promise<void> => {
    toy.setPick(null);
    let r: FortuneResult | null = null;
    app.store.update(['inventory', 'memories'], (s) => {
      r = drawFortune(s);
    });
    const won = r as FortuneResult | null;
    if (!won) return start();
    toy.lift(quarter);
    app.sfx.play('pop');
    setChildren(spell);
    if (!motion.reduced) await sleep(450);
    if (!alive()) return;
    if (won.tier !== 'common') app.sfx.play('win');
    prompt.textContent = t('Your fortune:');
    showTickets();
    const again = nextPayment(app.store.state);
    setChildren(
      result,
      prizeCard(app, won),
      collectionLine(app),
      again
        ? button(again === 'ticket' ? t('Play again ({n} tickets left)', { n: app.store.state.fortune.tickets }) : t('Play again ({n} coins)', { n: FORTUNE_RULES.drawCost }), { color: 'pink', sfx: app, onClick: start })
        : h('p', { class: 'hand-hint' }, t('No tickets and not enough coins left for now. Win some battles and come back!')),
    );
  };

  const done = modal(t('Paper Fortune Teller'), body, [{ label: t('Done'), value: 'ok', variant: 'primary', color: 'yellow' }], { className: 'fortune-modal' });
  start();
  await done;
}

/** Five quick draws at once (no folding): the five prize cards side by side. */
export async function openFiveDraws(app: App, ctx: NotebookCtx): Promise<void> {
  let won: FortuneResult[] | null = null;
  app.store.update(['inventory', 'memories'], (s) => {
    won = drawFive(s);
  });
  const list = won as FortuneResult[] | null;
  if (!list) {
    app.toast(t('Not enough coins (or draws left today) for five draws.'), 'info');
    return;
  }
  app.sfx.play(list.some((r) => r.tier === 'epic' || r.tier === 'legendary') ? 'win' : 'pop');
  const body = h('div', { class: 'ft-body ft-five' }, h('div', { class: 'ft-five-grid' }, list.map((r) => prizeCard(app, r))), collectionLine(app));
  await modal(t('Five fortunes!'), body, [{ label: t('Done'), value: 'ok', variant: 'primary', color: 'yellow' }], { className: 'fortune-modal' });
  ctx.refresh();
}
